function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
var __generator = this && this.__generator || function(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
};
import { ErrorReporterOriginatedError } from "./ErrorReporterOriginatedError";
// Any regex here will squelch matching error messages and not report them.
var SUPPRESSED_ERROR_REGEXES = [
    // A user trying to edit / read a notebook without access isn't noteworthy.
    /Error: User doesn't have (Write|Read) access to the kernel\./,
    /Error: Permission denied/,
    // Competition acceptance error is not exceptional.
    /To see the data you need to agree to the competition rules/,
    // Do not report 429 / 404 errors.
    /\[ResponseMiddlewares.enforceOk\] response was not ok \| (429|404)/,
    // Any useful data for this error is already lost at this point.
    // We should work on fixing this issue in Roslyn or at the middle tier.
    /Unexpected token e in JSON at position [0-9]+/,
    // stackdriver-errors-js throws some internal errors that we should not log.
    // 4xxs have a chance to loop (429 -> error -> report -> 429 -> ...)
    /Error: 4[0-9]+ http response on stackdriver report.*/,
    // Poor network conditions will not be resolved immediately.
    /Error: network error on stackdriver report.*/,
    // Cross-origin errors won't have stacktrace information available.
    /Uncaught Error: A cross-origin error was thrown.*/,
    // These errors have such generic traces that they aren't actionable. This is
    // likely from transient network issues. http://screen/32Tpq3in3sBZnZz
    /TypeError: Failed to fetch/,
    // Users are trying to include embedded google maps in their notebooks.
    /Error: Geocoding Service: You must use an API key to authenticate.*/, 
];
// Default sampling rate for the error reporter is to report 10% of errors
// observed. FE errors are far larger in volume than backend errors, since some
// errors are occuring per page load. Without sampling aggressively, we end up
// creating a lot of noise on the top-level dashboard, and use up reporting
// quota too quickly, which can prevent backend errors from logging.
var DEFAULT_SAMPLE_RATE = 0.1;
/**
 * A utility for logging errors to GCP Error Reporting (formerly Stackdriver).
 *
 * If you want an error logged, its recommended to send it manually via the
 * 'report' method on this class directly.
 *
 * FE errors can be viewed at go/kaggle-fe-errors. Do not use this class
 * directly, instead prefer canonical usage through the utility here:
 * @kaggle/components/common/ErrorReporter/errorReporter.ts
 */ export var GcpErrorReporter = /*#__PURE__*/ function() {
    "use strict";
    function GcpErrorReporter(config) {
        _classCallCheck(this, GcpErrorReporter);
        var key = config.key, projectId = config.projectId, service = config.service, version = config.version, userId = config.userId, _sampleRate = config.sampleRate, sampleRate = _sampleRate === void 0 ? DEFAULT_SAMPLE_RATE : _sampleRate;
        this.reportUrl = "https://clouderrorreporting.googleapis.com/v1beta1/projects/" + "".concat(projectId, "/events:report?key=").concat(key);
        this.serviceContext = {
            service: service,
            version: version
        };
        this.userId = userId;
        this.sampleRate = sampleRate;
    }
    var _proto = GcpErrorReporter.prototype;
    /**
   * Reports a JS error to GCP error reporting backends. Calling this directly
   * will always send an error report regardless of filtering or error sample
   * rate.
   */ _proto.report = function report(error) {
        var event = this.getReportedErrorEvent(error);
        // Sometimes stacktraces aren't available, such as cross-domain errors.
        if (!event.message) {
            return Promise.resolve();
        }
        return this.sendReportedErrorEvent(event);
    };
    /**
   * Similar to report, but applies filtering to reduce noise and respects
   * sample rate. Visible for testing, generally you want to call report if
   * manually reporting errors. This exists to be kinder to infra, if reporting
   * implicitly, as GCP API quota is shared across a GCP project, including
   * server-side error reporting.
   */ _proto.filteredReport = function filteredReport(error) {
        var shouldLogError = Math.random() < this.sampleRate;
        if (!shouldLogError) {
            return Promise.resolve();
        }
        var message = error.message;
        // Ignore all matching squelched messages.
        if (SUPPRESSED_ERROR_REGEXES.find(function(regex) {
            return regex.test(message);
        })) {
            return Promise.resolve();
        }
        // Ignore bots.
        if (!window.navigator || !window.navigator.userAgent || window.navigator.userAgent.toLowerCase().includes("bot")) {
            return Promise.resolve();
        }
        return this.report(error);
    };
    _proto.getReportedErrorEvent = function getReportedErrorEvent(error) {
        var _stack;
        return {
            context: {
                user: this.userId,
                httpRequest: {
                    userAgent: window.navigator.userAgent,
                    url: window.location.href
                }
            },
            // This is well supported and should be present on all modern browsers:
            // http://go/mdn/JavaScript/Reference/Global_Objects/Error/Stack
            // If for some reason this isn't set properly, the request will HTTP 400.
            message: (_stack = error.stack) !== null && _stack !== void 0 ? _stack : "Error stacktrace was unavailable",
            serviceContext: this.serviceContext
        };
    };
    _proto.sendReportedErrorEvent = function sendReportedErrorEvent(event) {
        var _this = this;
        return _asyncToGenerator(function() {
            var response, error, errorMessage;
            return __generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            2,
                            ,
                            3
                        ]);
                        return [
                            4,
                            fetch(_this.reportUrl, {
                                method: "post",
                                body: JSON.stringify(event),
                                headers: {
                                    "Content-Type": "application/json; charset=UTF-8"
                                }
                            })
                        ];
                    case 1:
                        response = _state.sent();
                        response.text().then(function(responseText) {
                            var succesfulStatus = response.status >= 200 && response.status < 300;
                            if (!succesfulStatus) {
                                var errorMessage = "An application error occurred when sending the error report. " + "HTTP status: ".concat(response.status, ", response: ").concat(responseText, ", ") + "report message: ".concat(event.message);
                                throw new ErrorReporterOriginatedError(errorMessage);
                            }
                        });
                        return [
                            3,
                            3
                        ];
                    case 2:
                        error = _state.sent();
                        if (_instanceof(error, Error)) {
                            errorMessage = "An error occurred when sending the error report: ".concat(error.message);
                            throw new ErrorReporterOriginatedError(errorMessage);
                        }
                        return [
                            3,
                            3
                        ];
                    case 3:
                        return [
                            2
                        ];
                }
            });
        })();
    };
    return GcpErrorReporter;
}();
